import Model from './BaseModel'
import Reseller from './Reseller'
import Module from './plans/Module'
import Plan from './Plan'
import Period from './Period'
import CancellationRequisition from './CancellationRequisition'
import i18n from '@/plugins/i18n'
import _ from 'lodash'

const TRIAL = 'trial'
const EXPIRED = 'expired'
const CANCELLED = 'canceled'
const CANCELLATION_REQUESTED = 'cancellation_requested'

export default class Customer extends Model {
    resourceName() {
        return 'customers'
    }

    fields() {
        return [
            'id',
            'reseller_id',
            'legal_name',
            'trade_name',
            'erp_contract_id',
            'erp_login_code',
            'cpf',
            'cnpj',
            'document',
            'discount',
            'email',
            'phone',
            'status',
            'blocked',
            'canceled',
            'created_at',
            'active_at',
            'blocked_at',
            'canceled_at',
            'deleted_at',
            'ignore',
            'readable_status',
            'can_ask_cancellation',
        ]
    }

    relationships() {
        return {
            reseller: new Reseller(),
            modules: new Module(),
            plans: new Plan(),
            periods: new Period(),
            cancellationRequisition: new CancellationRequisition(),
        }
    }

    dates() {
        return {
            created_at: 'YYYY-MM-DD',
            active_at: 'YYYY-MM-DD',
            blocked_at: 'YYYY-MM-DD',
            canceled_at: 'YYYY-MM-DD',
            deleted_at: 'YYYY-MM-DD',
        }
    }

    computed() {
        return {
            document(customer) {
                if (customer.cnpj) {
                    return customer.cnpj.replace(
                        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                        '$1.$2.$3/$4-$5',
                    )
                } else if (customer.cpf) {
                    return customer.cpf.replace(
                        /(\d{3})(\d{3})(\d{3})(\d{2})/,
                        '$1.$2.$3-$4',
                    )
                }

                return ''
            },

            formattedCreatedAt(customer) {
                return customer.created_at.isValid()
                    ? i18n.d(customer.created_at, 'datetime')
                    : null
            },

            formattedActiveAt(customer) {
                return customer.active_at.isValid()
                    ? i18n.d(customer.active_at, 'short')
                    : null
            },

            formattedCanceledAt(customer) {
                return customer.canceled_at.isValid()
                    ? i18n.d(customer.canceled_at, 'short')
                    : '-'
            },

            formattedDeletedAt(customer) {
                return customer.deleted_at.isValid()
                    ? i18n.d(customer.deleted_at, 'datetime')
                    : null
            },

            activePlan(customer) {
                if (!customer.plans.length) return null

                return _.findLast(customer.plans, function(each) {
                    return !each.deleted_at.isValid()
                })
            },

            trialUntil(customer) {
                return customer.created_at._isValid
                    ? i18n.d(customer.created_at.add(15, 'days'), 'short')
                    : null
            },
        }
    }

    get isDeleted() {
        return this.deleted_at.isValid()
    }

    get tableColumns() {
        return [
            {
                text: i18n.t('resources.customer.attributes.reseller_id'),
                value: 'reseller',
                sortable: false,
            },
            {
                text: i18n.tc('resources.customer.name', 1),
                value: 'trade_name',
            },
            {
                text: i18n.t('resources.customer.attributes.status'),
                value: 'readable_status',
            },
            {
                text: i18n.t('resources.customer.attributes.activated_at'),
                value: 'active_at',
            },
            {
                text: i18n.t('resources.customer.attributes.canceled_at'),
                value: 'formattedCanceledAt',
            },
            {
                text: i18n.t('tables.actions'),
                value: 'action',
                sortable: false,
                align: 'end',
            },
        ]
    }

    get actions() {
        return [
            {
                type: 'cancellationRequisitionLimitDay',
                icon: 'mdi-alert-octagon',
                color: 'info',
                label: i18n.t(
                    'actions.customer.cancellationRequisitionLimitDay.label',
                ),
                hide:
                    this.can_ask_cancellation === false ||
                    this.status == CANCELLED,
            },
            {
                type: 'cancellationRequisition',
                icon: 'mdi-alert-octagon',
                color: 'orange',
                label: i18n.t('actions.customer.cancellationRequisition.label'),
                hide: this.status !== CANCELLATION_REQUESTED,
            },
            {
                type: 'menu',
                actions: [
                    {
                        type: 'changeReseller',
                        label: i18n.t('actions.customer.changeReseller.label'),
                    },
                    {
                        type: 'sendResearch',
                        label: i18n.t('actions.customer.sendResearch.label'),
                    },
                    {
                        type: 'erpSync',
                        label: i18n.t('actions.customer.erpSync.label'),
                    },
                ],
            },
            {
                type: 'show',
                to: {
                    name: 'customer.show',
                    params: { id: this.id },
                },
            },
            {
                type: 'edit',
                permission: 'update customers',
                to: {
                    name: 'customer.edit',
                    params: { id: this.id },
                },
                hide: this.isDeleted,
            },
            {
                type: 'delete',
                disabled: !(
                    this.canceled_at.isValid() ||
                    this.status == EXPIRED ||
                    this.status == TRIAL
                ),
                hide: this.isDeleted,
            },
            {
                type: 'restore',
                hide: !this.isDeleted,
            },
        ]
    }

    async erpSync() {
        return this.makePersistRequest({
            url: this.getSelfUrl() + '/erpSync',
            method: 'PUT',
        })
    }

    async changeReseller(data) {
        return this.makePersistRequest({
            url: this.getSelfUrl() + '/change-reseller',
            method: 'PUT',
            data: data,
        })
    }

    async requestCancellation(reason) {
        return this.makePersistRequest({
            url: this.getSelfUrl() + '/request-cancellation',
            method: 'POST',
            data: {
                reason: reason,
                retained: false,
            },
        })
    }
}
